import * as React from 'react';
import {useRef, useEffect, useState, useContext} from 'react';
import {
  provider,
  useIsomorphicLayoutEffect,
  animate,
  AnimatePresence,
  view,
  useTokenValue,
  createView,
} from '@sail/engine';
import {tokens} from '../../tokens';
import {BaseDrawer, DrawerContext} from './BaseDrawer';
import {DrawerConfirmationDialog} from './DrawerConfirmationDialog';
import {useResizeObserver} from '../hooks/useResizeObserver';
import {fullscreenSheetStyles} from '../../utilStyles/sheet';
import {useUiConfig} from '../../providers/UiConfigProvider';
import type {DrawerProps} from './DrawerProps';

type DrawerStackContext = {
  register(): void;
  setChildDrawerWidth(width: number): void;
  unregister(): void;
};

const DrawerStackContext = React.createContext<DrawerStackContext | undefined>(
  undefined,
);

function useDrawerStack(drawerRef: React.RefObject<HTMLElement>) {
  const parentContext = useContext(DrawerStackContext);
  const [isStackParent, setIsStackParent] = useState(false);
  const [childDrawerWidth, setChildDrawerWidth] = useState<number>();
  const timeline = animate.useCurrentTimeline();

  const {current: context} = useRef<DrawerStackContext>({
    register() {
      setIsStackParent(true);
    },
    setChildDrawerWidth(width) {
      setChildDrawerWidth(width);
      parentContext?.setChildDrawerWidth(width);
    },
    unregister() {
      drawerRef.current?.addEventListener(
        'transitionstart',
        () => setChildDrawerWidth(undefined),
        {once: true},
      );

      setIsStackParent(false);
    },
  });

  // if there is a parent context, we know we're a stacked child drawer
  // so we should register with our parent drawer
  useIsomorphicLayoutEffect(() => {
    if (timeline && parentContext) {
      let unregistered = false;
      const unlisten = timeline.listen({
        in: () => parentContext.register(),
        out: () => {
          parentContext.unregister();
          unregistered = true;
        },
      });

      return () => {
        unlisten();
        if (!unregistered) {
          parentContext.unregister();
        }
      };
    }
  }, [parentContext, timeline]);

  // if a child registered its drawer with us, set its width
  // as a custom property so we can animate ourselves accordingly
  useIsomorphicLayoutEffect(() => {
    const drawer = drawerRef.current;
    if (drawer && childDrawerWidth) {
      drawer.style.setProperty('--child-drawer-width', `${childDrawerWidth}px`);

      return () => {
        drawer.style.removeProperty('--child-drawer-width');
      };
    }
  }, [childDrawerWidth]);

  // when a drawer becomes the upfront drawer (new child opens, or a child closes)
  // set ancestors --child-drawer-width to the upfront drawer's width
  useIsomorphicLayoutEffect(() => {
    const drawer = drawerRef.current;

    if (!isStackParent && drawer) {
      parentContext?.setChildDrawerWidth(drawer.clientWidth);
    }
  }, [isStackParent]);

  useResizeObserver({
    node: drawerRef,
    avoidInitialEvent: true,
    handler: React.useCallback(
      (entry) => {
        parentContext?.setChildDrawerWidth(entry.target.clientWidth);
      },
      [parentContext],
    ),
  });

  return {
    isStackParent,
    isStackChild: Boolean(parentContext),
    drawerStackProvider: provider(DrawerStackContext, context),
  };
}

export const DrawerDesktop = createView<DrawerProps>(
  ({
    children,
    confirmCloseMessages,
    preferredMobileDisplay,
    header,
    footer,
    onClose,
    ...props
  }) => {
    const [showConfirm, setShowConfirm] = useState(false);
    const space200 = useTokenValue(tokens.space[200]);
    const space150 = useTokenValue(tokens.space[150]);

    const drawerRef = useRef<HTMLDivElement>(null);
    const {isStackChild, isStackParent, drawerStackProvider} =
      useDrawerStack(drawerRef);
    const uiConfig = useUiConfig();
    const isSmallScreen = !!uiConfig?.isSmallScreen;

    useEffect(() => {
      drawerRef.current?.focus();
    }, [drawerRef]);

    const onConfirmClose = () => {
      if (confirmCloseMessages) {
        setShowConfirm(true);
      } else {
        onClose && onClose();
      }
    };

    function onKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onConfirmClose();
      }
    }

    const easing = useTokenValue(tokens.animation.easing) as string;

    const durationShort = parseInt(
      useTokenValue(tokens.animation.duration.short) as string,
      10,
    );

    const durationMedium = parseInt(
      useTokenValue(tokens.animation.duration.medium) as string,
      10,
    );

    const durationLong = parseInt(
      useTokenValue(tokens.animation.duration.long) as string,
      10,
    );

    const desktopTransformAnimations = (disabled: boolean) => [
      animate.in({
        duration: isStackChild ? durationMedium : durationShort,
        transform: isStackChild
          ? 'translateX(calc(100% * var(--drawer-direction, 1)))'
          : 'translateX(calc(24% * var(--drawer-direction, 1)))',
        easing,
        disabled,
      }),
      animate.out({
        duration: durationLong,
        transform: 'translateX(calc(120% * var(--drawer-direction, 1)))',
        opacity: 1,
        easing,
        disabled,
      }),
    ];

    return (
      <BaseDrawer
        // eslint-disable-next-line react/jsx-no-bind
        onKeyDown={onKeyDown}
        preferredMobileDisplay={preferredMobileDisplay}
        {...props}
        ref={drawerRef}
        onClose={onConfirmClose}
        uses={[
          provider(DrawerContext, {onClose: onConfirmClose}),
          drawerStackProvider,
          animate.in({
            duration: isStackChild ? 0 : durationShort,
            opacity: 0.8,
            easing,
          }),
          animate
            .transition({
              duration: durationShort,
              transform: `
              translateX(calc((100% - var(--child-drawer-width, 0) - ${space200}) * var(--drawer-direction, 1)))
                perspective(57.2mm)
                translateZ(calc(${space150} / -2))`,
            })
            .when(isStackParent),
          ...desktopTransformAnimations(false),
          ...(isSmallScreen ? [fullscreenSheetStyles] : []),
        ]}
        header={header}
      >
        <view.div
          css={{
            position: 'relative',
            overflowY: 'auto',
            overscrollBehaviorY: 'contain',
            flexShrink: 1,
          }}
        >
          {children}
        </view.div>
        <view.div css={{height: 'fit'}}>{footer}</view.div>
        <AnimatePresence>
          {confirmCloseMessages && showConfirm ? (
            <DrawerConfirmationDialog
              messages={confirmCloseMessages}
              onClose={onClose}
              onCancel={() => setShowConfirm(false)}
            />
          ) : null}
        </AnimatePresence>
      </BaseDrawer>
    );
  },
);
